import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import AgenciaModal from './components/modals/AgenciaModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = { idEscola: '', idBanco: '', idAgencia: '', cidade: '', uf: '' };

function Agencias() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [ufs, setUfs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { bancos } = useReferenceData(token, null);
  const displayed = items.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    axios.get(`${API_BASE_URL.UF}/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUfs(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (form.uf) {
      axios.get(`${API_BASE_URL.MUNICIPIO}/uf/${form.uf}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCidades(r.data)).catch(() => setCidades([]));
    } else {
      setCidades([]);
    }
  }, [form.uf, token]);

  useEffect(() => {
    setIsLoading(true);
    axios.get(`${API_BASE_URL.AGENCIA}/findByEscola`, {
      params: { idEscola: cnpj }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        setItems(r.data);
      })
      .catch(err => {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar agências.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => {
      const updated = { ...f, [name]: value };
      if (name === 'uf') updated.cidade = '';
      return updated;
    });
  }

  function handleNew() { setForm({ ...emptyForm, idEscola: cnpj || '' }); setShowModal(true); }
  function handleEdit() {
    if (selected.length !== 1) return;
    setForm({ ...selected[0] });
    setShowModal(true);
  }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.AGENCIA}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar agência.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () =>
      Promise.all(selected.map(s => axios.delete(`${API_BASE_URL.AGENCIA}/delete`, {
        params: { idEscola: s.idEscola, idBanco: s.idBanco, idAgencia: s.idAgencia },
        headers: { Authorization: `Bearer ${token}` }
      }))).then(() => { setSelected([]); setAtualizar(a => !a); })
        .catch(() => showDefaultModal('error', 'Erro ao excluir agência.'))
    );
  }

  function toggleSelect(item) {
    const key = `${item.idBanco}_${item.idAgencia}`;
    setSelected(prev => prev.find(s => `${s.idBanco}_${s.idAgencia}` === key) ? [] : [item]);
  }

  function bancoLabel(idBanco) {
    const b = bancos.find(b => String(b.id) === String(idBanco));
    return b ? `${b.id} - ${b.nome}` : idBanco;
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="AGÊNCIAS BANCÁRIAS" icon="bi-buildings" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
          </Toolbar>
          <TableContainer isLoading={false}>
                              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>Banco</th><th>Agência</th><th>Cidade</th><th>UF</th>
                </tr>}>
                <tbody>
                  {displayed.map((item, idx) => {
                    const key = `${item.idBanco}_${item.idAgencia}`;
                    return (
                      <tr key={key}
                        className={selected.find(s => `${s.idBanco}_${s.idAgencia}` === key) ? 'table-success' : ''}
                        onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                        <td>{setPagination.startIndex + idx + 1}</td>
                        <td>{bancoLabel(item.idBanco)}</td>
                        <td>{item.idAgencia}</td>
                        <td>{item.cidade}</td>
                        <td>{item.uf}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <AgenciaModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} bancos={bancos} ufs={ufs} cidades={cidades} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Agencias;
